import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, MenuItem, Stack, CircularProgress } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ClientContactDTO,
  ClientDTO,
  ClosureDTO,
  createClosure,
  FeeArrangementDTO,
  FieldTypeEnum,
  getAllClosureCustomFields,
  getAllFeeArrangementsForClient,
  getCurrencyList,
  getPlacementOriginList,
  getPlacementStatusList,
  IJob,
  IJobSearchFilterDTO,
  LogAction,
  LogContextType,
  LogEntityType,
  PlacementOriginDTO,
  PlacementStatusDTO,
  saveActivityLog,
  updateClosure,
} from 'wisestep-api';
import {
  ControlledTextInput,
  Label,
  WsButton,
  WsChip,
  WsChipVariants,
  WsDatePicker,
  WsLocationSearch,
  WsSelect,
  WsSelectObj,
} from 'wisestep-base';
import { object } from 'yup';

import { ClientAutoCompleteFilter } from '../../../components';
import ClientContactAutoComplete from '../../../components/clientContactAutoComplete';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import WsTeamFilter from '../../../components/filters/components/WsTeamsFilter';
import { SelectWCreate } from '../../../components/SelectWCreate';
import {
  getAvEditAddDesUiName,
  activityLogDTOHelper,
} from '../../../features/activityLog/ActivityHelper';
import { useClientAuthz, ClientAction } from '../../../hooks/useClientAuthz';
import { useFetch } from '../../../hooks/useFetch';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import CustomField from '../../clientListingPage/components/CustomField';
import CreateNewClientContactDialog from '../../jobs/post/components/CreateNewClientContactDialog';
import CreateNewClientDialog from '../../jobs/post/components/CreateNewClientDialog';
import { jobSearchPayload } from '../helper/payload';

import AddFeeArrangementDialog from './AddFeeArrangementDialog';
import ClosureJobPopup from './ClosureJobPopup';

interface IComponentProps {
  setClosureDTO: (data: ClosureDTO) => void;
  currentClosureDTO: ClosureDTO;
  onCancel: () => void;
  getDirtyFieldsValue: any;
}

const isValidObj = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

const CreatePlacementForm = ({
  setClosureDTO,
  currentClosureDTO,
  onCancel,
  getDirtyFieldsValue,
}: IComponentProps) => {
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const validationSchema = object().shape({});
  const isAuthorizedAddClient = useClientAuthz(ClientAction.ADD_CLIENT_DETAILS);
  const [companyNameNewVal, setCompanyNameNewVal] = useState<string>('');
  const [openNewClientDialogue, setOpenNewClientDialogue] = useState<boolean>(false);
  const [companyContactInput, setCompanyContactInput] = useState<string>('');
  const [openClientContactDialog, setOpenClientContactDialog] = useState<boolean>(false);
  const [currencyList, setCurrencyList] = useState<WsSelectObj[]>([]);
  const [openFADialog, setOpenFADialog] = useState<boolean>(false);
  const [openJobPopup, setOpenJobPopup] = useState<boolean>(false);
  const [feeArrangementOptions, setFeeArrangementOptions] = useState<WsSelectObj[]>([]);
  const [jobSearchDTO, setJobSearchDTO] = useState<IJobSearchFilterDTO>({ ...jobSearchPayload });
  const [ourPlacementStatusOptions, setOurPlacementStatusOptions] = useState<WsSelectObj[]>([]);
  const [ourPlacementOriginOptions, setOurPlacementOriginOptions] = useState<WsSelectObj[]>([]);
  const [defaultPlacementStatusId, setDefaultPlacementStatusId] = useState<string>('');
  const [feeArrangementList, setFeeArrangementList] = useState<FeeArrangementDTO[]>([]);
  const [selectedJobName, setSelectedJobName] = useState<string>('');
  const [isMarkInactive, setIsMarkInactive] = useState<boolean>(false);
  const [customFields] = useFetch(getAllClosureCustomFields);
  const [isLoading, setIsLoading] = useState(false);

  const getDefaultValues = (): ClosureDTO => {
    const responseDTO = { ...currentClosureDTO };
    responseDTO.accountManager = isValidObj(currentClosureDTO.accountManager)
      ? JSON.parse(currentClosureDTO.accountManager)
      : '';
    if (currentClosureDTO.team) {
      responseDTO.team = isValidObj(currentClosureDTO.team)
        ? JSON.parse(currentClosureDTO.team)
        : null;
    }
    return responseDTO;
  };

  const defaultValues = getDefaultValues();
  const methods = useForm<ClosureDTO>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...defaultValues,
      customFields: defaultValues.customFields ?? {},
    },
  });

  const {
    setValue,
    watch,
    formState: { dirtyFields },
    register,
  } = methods;

  const clientName = watch('clientName') ?? '';
  const clientRecruiterOne = watch('clientRecruiterOne');

  const openCloseFADialog = (flag: boolean) => {
    setOpenFADialog(flag);
  };

  register('clientName', {
    onChange: (e) => {
      const clientName = e.target.value;
      setJobSearchDTO({
        ...jobSearchPayload,
        clientName: clientName.includes('->') ? clientName.split('->')[1].trim() : clientName,
      });
      getFeeArrangementData();
    },
  });

  register('feeArrangementId', {
    onChange: (e) => {
      const feeArrangementId = e.target.value;
      calculatGauranteeDaysAndComissionValue(feeArrangementId);
    },
  });

  register('compensationAmount', {
    onChange: (e) => {
      const compensationAmount = e.target.value;
      const feeArrangementId = methods.getValues('feeArrangementId');
      if (feeArrangementId) {
        const selectedFeeArrangement: FeeArrangementDTO | undefined = feeArrangementList.find(
          (e) => e.value === feeArrangementId
        );
        if (selectedFeeArrangement && selectedFeeArrangement.feeType === 'FIXED') {
          methods.setValue('commissionValue', selectedFeeArrangement.fee);
        } else if (selectedFeeArrangement && selectedFeeArrangement.feeType === 'PERCENTAGE') {
          const commissionValue =
            (parseFloat(compensationAmount) * selectedFeeArrangement.fee) / 100;
          methods.setValue('commissionValue', commissionValue);
        }
      }
    },
  });

  const calculatGauranteeDaysAndComissionValue = (
    feeArrangementId: string,
    selectedFeeArrangement?: FeeArrangementDTO
  ) => {
    if (feeArrangementId) {
      if (!selectedFeeArrangement) {
        selectedFeeArrangement = feeArrangementList.find((e) => e.value === feeArrangementId);
      }
      methods.setValue(
        'feeArrangementAmount',
        selectedFeeArrangement ? selectedFeeArrangement.fee : 0
      );
      const employmentGuarantee = selectedFeeArrangement?.employmentGuarantee;
      if (employmentGuarantee && selectedFeeArrangement) {
        const empGuaranteeDays =
          ({ DAYS: 1, WEEKS: 7, MONTHS: 30 }[selectedFeeArrangement?.empGuaranteePeriodUnit] ?? 0) *
          employmentGuarantee;
        methods.setValue('employmentGuaranteeDays', empGuaranteeDays);
        setGuaranteeEndDate(empGuaranteeDays);
      }
      if (selectedFeeArrangement && selectedFeeArrangement.feeType === 'PERCENTAGE') {
        const commissionValue =
          (methods.getValues('compensationAmount') * selectedFeeArrangement.fee) / 100;
        methods.setValue('commissionValue', commissionValue);
      } else {
        methods.setValue('commissionValue', selectedFeeArrangement?.fee ?? 0);
      }
    }
  };

  const setGuaranteeEndDate = (days: number) => {
    if (currentClosureDTO.joiningDate) {
      const date = new Date(currentClosureDTO.joiningDate);
      date.setDate(date.getDate() + days);
      methods.setValue('employmentGuaranteeEndDate', date);
    }
  };

  const onClientCreateFromDialogue = (selectedClient: ClientDTO) => {
    setValue('clientName', selectedClient?.name);
    setValue('clientId', selectedClient.id);
    setOpenNewClientDialogue(false);
  };

  const onClientContactCreateFromDialog = (selectedClientRecruiter: ClientContactDTO) => {
    const fullName = selectedClientRecruiter?.firstName
      .concat(' ' + selectedClientRecruiter?.lastName)
      .trim();

    setValue('clientRecruiterOne', `${fullName} - ${selectedClientRecruiter.emailAddress}`);
    setValue('clientContactId', selectedClientRecruiter?.id ?? '');
    // setValue('clientRecruiterName', fullName);
    setOpenClientContactDialog(false);
  };

  const getFeeArrangementData = async () => {
    const response = await getAllFeeArrangementsForClient(methods.getValues('clientId'));
    setFeeArrangementList(response.data);
    setFeeArrangementOptions(
      response.data.map((fa: FeeArrangementDTO) => ({
        label: fa.contractType + ' - ' + fa.fee + (fa.feeType === 'PERCENTAGE' ? '%' : ''),
        value: fa?.value,
      }))
    );
  };

  useEffect(() => {
    const getOurPlacementStatusList = async () => {
      const response = await getPlacementStatusList();
      const optionsArray: WsSelectObj[] = [];
      response.data.forEach((obj: PlacementStatusDTO) => {
        optionsArray.push({ label: obj.placementStatusName as string, value: obj.id ?? '' });
      });
      const statusDefaultId = optionsArray.find((e) => e.label === 'Placed');
      setOurPlacementStatusOptions(optionsArray);
      setDefaultPlacementStatusId(statusDefaultId?.value as string);
    };

    const getAllPlacementOrigins = async () => {
      const response = await getPlacementOriginList();
      const optionsArray: WsSelectObj[] = [];
      response.data.forEach((obj: PlacementOriginDTO) => {
        if (obj.visibility) {
          optionsArray.push({ value: obj.id ?? '', label: obj.originName as string });
        }
      });
      setOurPlacementOriginOptions(optionsArray);
    };

    const getCurrencyData = async () => {
      const response = await getCurrencyList();
      setCurrencyList(
        response.data.map((currency) => ({
          label: currency?.symbol,
          value: currency?.id,
        }))
      );
    };
    getCurrencyData();
    getOurPlacementStatusList();
    getAllPlacementOrigins();
    if (currentClosureDTO.feeArrangementId) {
      getFeeArrangementData();
    }
    setSelectedJobName(currentClosureDTO.positionTitle);
  }, []);

  const setFeeArrangement = (data: FeeArrangementDTO) => {
    getFeeArrangementData();
    if (data.id) {
      methods.setValue('feeArrangementId', data.id);
      calculatGauranteeDaysAndComissionValue(data.id, data);
    }
  };

  const periodOptions = [
    {
      label: 'Hourly',
      value: 'HOURLY',
    },
    {
      label: 'Weekly',
      value: 'WEEKLY',
    },
    {
      label: 'Monthly',
      value: 'MONTHLY',
    },
    {
      label: 'Annually',
      value: 'ANNUALLY',
    },
    {
      label: 'Once',
      value: 'ONCE',
    },
  ];

  const onSubmit = async (data: ClosureDTO) => {
    setIsLoading(true);
    data.accountManager = data.accountManager ? JSON.stringify(data.accountManager) : '';
    data.clientRecruiterEmail = clientRecruiterOne
      ? clientRecruiterOne.split(' - ')[1].trim()
      : null;
    if (!data.placementStatusId) data.placementStatusId = defaultPlacementStatusId;
    if (Array.isArray(data.team)) {
      const teamArray = data.team;
      data.team = teamArray ? JSON.stringify(teamArray[0]) : '';
    } else {
      data.team = data.team ? JSON.stringify(data.team) : '';
    }
    if (data.placementStatusId) setClosureDTO(data);
    try {
      const avPayload = { ...activityLogDTOHelper };
      const avChangedValueViaDirtyFileds = Object.keys({ ...getDirtyFieldsValue, ...dirtyFields })
        .toString()
        .split(',');

      let response;
      if (data.id) {
        response = await updateClosure(data);
        avPayload.action = LogAction.Edit;
        const avEmptyEntityfields = Object.keys(
          Object.entries(currentClosureDTO).reduce((acc, [k, v]) => {
            if (v === '' || v === null) acc[k] = v;
            return acc;
          }, {})
        );

        if (data.positionTitle !== currentClosureDTO.positionTitle) {
          avChangedValueViaDirtyFileds.push('positionTitle');
        }

        const dataAccountManager = !isEmpty(data.accountManager)
          ? JSON.parse(data.accountManager).id
          : null;

        const currentAccountManager = !isEmpty(currentClosureDTO.accountManager)
          ? JSON.parse(currentClosureDTO.accountManager).id
          : null;

        if (!isEmpty(dataAccountManager) && dataAccountManager != currentAccountManager) {
          avChangedValueViaDirtyFileds.push('accountManager');
        }

        const dataTeam = !isEmpty(data.team) ? JSON.parse(data.team).id : '';

        const currentTeam = !isEmpty(currentClosureDTO.team)
          ? JSON.parse(currentClosureDTO.team).id
          : '';

        if (!isEmpty(dataTeam) && dataTeam != currentTeam) {
          avChangedValueViaDirtyFileds.push('team');
        }

        avPayload.description = `${getAvEditAddDesUiName(
          'Placement',
          avEmptyEntityfields,
          avChangedValueViaDirtyFileds
        )} under Placement`;

        avPayload.parentEntitiyType = LogContextType.Placement;
        avPayload.parentEntitiyId = data.id;
        avPayload.entityId = data.id;
      } else {
        response = await createClosure(data);
        avPayload.action = LogAction.Add;
        avPayload.entityId = response?.data?.value;
        avPayload.description = `ADDED ${data.candidateName} for Placement under Client`;
        avPayload.parentEntitiyType = LogContextType.Client;
        avPayload.parentEntitiyId = data.clientId;
      }
      if (response) {
        avPayload.entityType = LogEntityType.Placement;
        avPayload.field = `Placement`;
        avPayload.entityName = data.candidateName;
        avPayload.contextData = {
          clientId: data.clientId,
          clientContactId: data.clientContactId,
          currencyId: data.currencyId,
          feeArrangementId: data.feeArrangementId,
          ourEntityId: data.ourEntityId,
          positionId: data.positionId,
          positionTitle: data.positionTitle,
          feeArrangement: data.feeArrangement,
        };
        avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
        navigate('/placements/' + response?.data?.value, {
          state: { placementType: 'PERMANENT' },
        });
      }
      setNotification(success(`Placement details successfully saved.`));
    } catch (error) {
      setNotification(failed(`Something went wrong while saving placement details.`));
    }
    setIsLoading(false);
  };

  const setSelectedJobDetail = (job: IJob) => {
    setSelectedJobName(job.title as string);
    methods.setValue('positionId', job?.id ?? '');
    methods.setValue('clientContactId', job?.clientContactId);
    methods.setValue('positionTitle', job?.title ?? '');
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box borderRadius={1.5}>
            <Grid container py={1}>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="candidateName"
                  type="text"
                  fullWidth
                  labelText="Candidate Name"
                  variant="outlined"
                  showMandatorySymbol
                  disabled={currentClosureDTO.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsLocationSearch
                  size="small"
                  placeHolder="Candidate Location"
                  name="candidateLocation"
                  variant="outlined"
                  multiSelect={false}
                  showMandatorySymbol={true}
                  disabled={currentClosureDTO.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="candidateEmail"
                  type="text"
                  fullWidth
                  labelText="Candidate Email"
                  variant="outlined"
                  showMandatorySymbol
                  disabled={currentClosureDTO.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="candidateContact"
                  type="text"
                  fullWidth
                  labelText="Candidate Contact"
                  variant="outlined"
                  disabled={currentClosureDTO.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <>
                  {isAuthorizedAddClient ? (
                    <ClientAutoCompleteFilter
                      key={clientName}
                      name="clientName"
                      isPlacement={true}
                      clientIdFieldName="clientId"
                      textForAddingNew="Client"
                      showMandatorySymbol
                      onNewValueCreate={(data: string) => {
                        setValue('clientName', ' ');
                        setCompanyNameNewVal(data ?? '');
                        setOpenNewClientDialogue(true);
                      }}
                      onStatusUpdateValue={(data: string) => {
                        setValue('clientName', ' ');
                        setCompanyNameNewVal(data ?? '');
                        setIsMarkInactive(true);
                        setOpenNewClientDialogue(true);
                      }}
                    />
                  ) : (
                    <ClientAutoCompleteFilter
                      key={clientName}
                      name="clientName"
                      isPlacement={true}
                      clientIdFieldName="clientId"
                      showMandatorySymbol
                    />
                  )}
                </>
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ClientContactAutoComplete
                  key={clientRecruiterOne}
                  label="Client Contact"
                  name="clientRecruiterOne"
                  clientId={methods.getValues('clientId')}
                  disabled={false}
                  textForAddingNew="Client Contact"
                  clientName={clientName}
                  clientContactIdFieldName="clientRecruiterIdOne"
                  placeholder="Search client contact"
                  onNewValueCreate={(data: string) => {
                    setValue('clientRecruiterOne', '');
                    setCompanyContactInput(data);
                    setOpenClientContactDialog(true);
                  }}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsButton variant="contained" fullWidth onClick={() => setOpenJobPopup(true)}>
                  Tag Job
                </WsButton>
              </Grid>
              <Grid item p={1} sm={5.75}>
                {selectedJobName && (
                  <WsChip value={selectedJobName} variant={WsChipVariants.TERTIARY} />
                )}
              </Grid>
              <Grid item p={1} sm={5.75}>
                <Label>Team</Label>
                <WsTeamFilter
                  name="team"
                  placeHolder="Search Team"
                  multiple={false}
                  requireTeam={false}
                  defaultValue={
                    methods.getValues('team') ? [methods.getValues('team')['text']] : []
                  }
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <Label>Account Manager</Label>
                <RecruiterSuggestions
                  name="accountManager"
                  multiple={false}
                  requireRecruiter={true}
                  placeholder="Search account manager"
                  defaultValue={
                    methods.getValues('accountManager')
                      ? [methods.getValues('accountManager')['text']]
                      : []
                  }
                />
              </Grid>
              <Grid container item p={1} sm={5.75}>
                <Grid item sm={2.5}>
                  <WsSelect
                    label="Currency"
                    name="currencyId"
                    selectArray={currencyList}
                    size="small"
                  />
                </Grid>
                <Grid pl={1} pr={1} item sm={6.75}>
                  <ControlledTextInput
                    name="compensationAmount"
                    type="text"
                    fullWidth
                    labelText="Compensation Amount"
                    variant="outlined"
                    showMandatorySymbol
                  />
                </Grid>
                <Grid item sm={2.75}>
                  <WsSelect
                    label="Period"
                    name="period"
                    selectArray={periodOptions}
                    size="small"
                    showMandatorySymbol
                  />
                </Grid>
              </Grid>
              <Grid item p={1} sm={5.75}>
                <Label>Joining Date*</Label>
                <WsDatePicker name="joiningDate" label="" required={true} />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <SelectWCreate
                  onCreate={() => {
                    openCloseFADialog(true);
                  }}
                  name="feeArrangementId"
                  defaultValue={currentClosureDTO?.feeArrangementId ?? ''}
                  label={'Fee Arrangement'}
                  showMandatorySymbol={true}
                >
                  {feeArrangementOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SelectWCreate>
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="feeArrangementAmount"
                  type="text"
                  fullWidth
                  labelText="Arrangement Value"
                  variant="outlined"
                  showMandatorySymbol
                  disabled
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="commissionValue"
                  type="text"
                  fullWidth
                  labelText="Commission Value"
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="employmentGuaranteeDays"
                  type="text"
                  fullWidth
                  labelText="Employment Guarantee Days"
                  variant="outlined"
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                {defaultPlacementStatusId && (
                  <WsSelect
                    label="Placement Status"
                    name="placementStatusId"
                    selectArray={ourPlacementStatusOptions}
                    defaultValue={defaultPlacementStatusId}
                    size="small"
                  />
                )}
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsSelect
                  label="Placement Origin"
                  name="placementOrigin"
                  selectArray={ourPlacementOriginOptions}
                  size="small"
                />
              </Grid>
              {customFields?.map(
                (field) =>
                  field.visible && (
                    <Grid
                      item
                      key={field.id}
                      p={1}
                      sm={field.type === FieldTypeEnum.Checkbox ? 11.5 : 5.75}
                    >
                      <CustomField {...field} name={`customFields.${field.name}`} />
                    </Grid>
                  )
              )}
            </Grid>
          </Box>
          <Stack
            p={1}
            spacing={2}
            direction="row"
            sx={{ display: 'flex', justifyContent: 'right' }}
          >
            <WsButton variant="outlined" size="medium" type="button" onClick={onCancel}>
              {`Cancel`}
            </WsButton>
            <WsButton
              variant="contained"
              size="medium"
              type="submit"
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : undefined}
            >
              {isLoading ? 'Saving...' : `Add Placement`}
            </WsButton>
          </Stack>
        </form>
      </FormProvider>
      {openNewClientDialogue && (
        <CreateNewClientDialog
          open={openNewClientDialogue}
          clientNameInputVal={companyNameNewVal}
          onClose={setOpenNewClientDialogue}
          onSelect={onClientCreateFromDialogue}
          isMarkInactive={isMarkInactive}
        />
      )}
      {openClientContactDialog && clientName && (
        <CreateNewClientContactDialog
          open={openClientContactDialog}
          clientId={methods.getValues('clientId')}
          clientName={clientName ?? ''}
          clientRecruiterEmailInputVal={companyContactInput ?? ''}
          onClose={() => setOpenClientContactDialog(false)}
          onSelect={onClientContactCreateFromDialog}
        />
      )}
      {openFADialog && (
        <AddFeeArrangementDialog
          clientId={currentClosureDTO.clientId}
          openDialog={openFADialog}
          handleClose={() => openCloseFADialog(false)}
          onSave={(data) => setFeeArrangement(data)}
        />
      )}
      {openJobPopup && (
        <ClosureJobPopup
          setOpenJobPopup={setOpenJobPopup}
          setSelectedJob={setSelectedJobDetail}
          jobSearchDTO={jobSearchDTO}
        />
      )}
    </>
  );
};

export default CreatePlacementForm;
